/* eslint-disable react-hooks/exhaustive-deps */
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Icon,
  SearchInput,
  Space,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { CloseSIcon } from '@tonic-one/react-icons';
import { useToggle } from '@tonic-one/react-hooks';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DelModal from 'src/pages/Report/ActionModal/del';
import { v4 } from 'uuid';

import { GlobalApi } from '@Apis';
import { useDisclosure } from '../../../components/Common/Tooltip/TextEllipsis/utils';
import IconButton from '../../../components/IconButton';
import {
  PasswordExtractionRulesItem,
  InputPasswordExtractionRulesItem,
  PasswordExtractionRulesProps,
} from '../../../types/globalSetting';
import { char2entity } from '../../../utils/common';
import LoadingBtn from '../../Report/LoadingBtn';
import SmartTooltip from '../../Report/SmartTooltip';
import { AddModal } from './AddModal';
import { EditModal } from './EditModal';
import { usePermissionStore } from '@Models/usePermissionModel';
import { PermissionNameEnum } from '@Types/permissions';

const AttachmentPasswordGuessing = () => {
  const { widthEditablePermission } = usePermissionStore();
  const withEditPermission = widthEditablePermission(
    PermissionNameEnum.administrator
  );
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  const [items, setItems] = useState<PasswordExtractionRulesItem[] | []>([]);
  const [allItems, setAllItems] = useState<PasswordExtractionRulesItem[] | []>(
    []
  );
  const [selectedExtractionRule, setSelectedExtractionRule] = useState<
    PasswordExtractionRulesItem[] | []
  >([]);
  const [delModalTitle, setDelModalTitle] = useState('Delete Approved Users');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isAddModalOpen, toggleAddModalOpen] = useToggle(false);
  const [isEditModalOpen, toggleEditModalOpen] = useToggle(false);
  const [delModalContent, setDelModalContent] = useState<
    JSX.Element[] | undefined
  >(undefined);
  const [delLoading, setDelLoading] = useState(false);
  const search = (keyword: string) => {
    if (keyword !== '') {
      const temp = allItems.filter(
        (item) => item.prefix.toLowerCase().indexOf(keyword.toLowerCase()) > -1
      );
      setItems(temp);
    } else {
      setItems(allItems);
    }
  };
  const numberRule = 10;
  const hoverBackgroundColor = (
    {
      dark: 'rgba(255, 255, 255, 0.12)',
      light: 'rgba(0, 0, 0, 0.12)',
    } as any
  )[colorMode];
  const [enablePasswordExtraction, setPasswordExtraction] =
    useState<boolean>(false);

  async function loadData() {
    try {
      const data: PasswordExtractionRulesProps = (
        await GlobalApi.getPasswordExtractionRules()
      ).data;
      data.passwordExtractionRules.map((item) => {
        item.matchPart = char2entity(item.matchPart);
        item.prefix = char2entity(item.prefix);
        item.suffix = char2entity(item.suffix);
        return item;
      });
      setPasswordExtraction(data.passwordExtractionEnable);
      setItems(data.passwordExtractionRules);
      setAllItems(data.passwordExtractionRules);
    } catch (error: any) {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    }
  }
  async function switchStatus(enablePasswordExtraction: boolean) {
    const res = await GlobalApi.updatePasswordExtractionRulesEnabledStatus(
      Number(enablePasswordExtraction)
    );
    if (res.data) {
      setPasswordExtraction(enablePasswordExtraction);
      window.showNotification({
        titleMsg: t('globalSetting.switch.success'),
        severity: 'success',
      });
    } else {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    }
  }
  useEffect(() => {
    (async function doCall() {
      await loadData();
    })();
  }, []);

  const columns: ColumnDef<PasswordExtractionRulesItem>[] = useMemo(
    () => [
      {
        id: 'selection',
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            indeterminate={row.getIsSomeSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
        size: 48,
      },
      {
        id: 'prefix',
        header: t(
          'globalSetting.AttachmentPasswordGuessing.column.precedingText'
        ),
        accessorKey: 'prefix',
        size: 300,
      },
      {
        id: 'matchPart',
        header: t('globalSetting.AttachmentPasswordGuessing.column.password'),
        accessorKey: 'matchPart',
        size: 300,
      },
      {
        id: 'suffix',
        header: t(
          'globalSetting.AttachmentPasswordGuessing.column.subsequentText'
        ),
        accessorKey: 'suffix',
        size: 300,
      },
    ],
    []
  );
  const [rowSelection, setRowSelection] = useState({});
  const table = useReactTable({
    data: items,
    columns,
    defaultColumn: {
      minSize: 40,
    },
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.itemKey,
    state: {
      rowSelection,
    },
    enableRowSelection: true, // enable row selection for all rows
    // enableRowSelection: row => row.original.detections > 0, // or enable row selection conditionally per row
    onRowSelectionChange: setRowSelection,
  });
  const cellHeight = 36;
  const headerHeight = 36;
  const rowsToDisplay = 10;
  const tableStyleProps = {
    height: headerHeight + rowsToDisplay * cellHeight,
  };
  const selectedRowCount = Object.keys(rowSelection).length;
  const onDelete = () => {
    setDelModalTitle(
      t('globalSetting.AttachmentPasswordGuessing.delete.title')
    );
    const selectedRows = getSelectedRows();
    const content = selectedRows.map((item) => (
      <Text key={item.itemKey} mb={8}>
        {`${char2entity(item.prefix)} ${char2entity(
          item.matchPart
        )} ${char2entity(item.suffix)}`}
      </Text>
    ));
    setDelModalContent(content);
    onOpen();
  };
  const reset = () => {
    setRowSelection({});
  };

  function getSelectedRows() {
    const selectedRows: PasswordExtractionRulesItem[] = [];
    for (const key of Object.keys(rowSelection)) {
      const item = items.find((item) => item.itemKey === key);
      if (item) {
        selectedRows.push(item);
      }
    }
    return selectedRows;
  }

  const onAdd = () => {
    toggleAddModalOpen();
  };
  const onEdit = () => {
    const selectedRows = getSelectedRows();
    setSelectedExtractionRule(selectedRows);
    toggleEditModalOpen();
  };
  const handleAdd = async (value: PasswordExtractionRulesItem) => {
    const param = {
      prefix: char2entity(value.prefix),
      matchPart: char2entity(value.matchPart),
      suffix: char2entity(value.suffix),
      ignoreCase: value.ignoreCase,
      itemKey: v4(),
    };
    const res = await GlobalApi.addPasswordExtractionRules({
      passwordExtractionRules: [param],
    });
    if (res.data) {
      // window.showNotification({
      //   titleMsg: t('globalSetting.AttachmentPasswordGuessing.add.success'),
      //   severity: 'success',
      // });
    }
    toggleAddModalOpen();
    await loadData();
  };
  const handleEdit = async (value: PasswordExtractionRulesItem) => {
    const param = {
      prefix: char2entity(value.prefix),
      matchPart: char2entity(value.matchPart),
      suffix: char2entity(value.suffix),
      ignoreCase: value.ignoreCase,
      itemKey: char2entity(value.itemKey),
    };
    const res = await GlobalApi.editPasswordExtractionRules({
      passwordExtractionRules: [param],
    });
    if (res.data) {
      // window.showNotification({
      //   titleMsg: t('globalSetting.AttachmentPasswordGuessing.edit.success'),
      //   severity: 'success',
      // });
    }
    toggleEditModalOpen();
    await loadData();
  };

  const confirmDel = async () => {
    setDelLoading(true);
    if (!selectedRowCount) return;
    try {
      const selectedRows = getSelectedRows();
      const selectedData: PasswordExtractionRulesItem[] = [];
      selectedRows.map((item) => {
        const selectedValue: InputPasswordExtractionRulesItem = {
          itemKey: item.itemKey,
          ignoreCase: item.ignoreCase,
          matchPart: item.matchPart,
          prefix: item.prefix,
          suffix: item.suffix,
        };
        const _params = JSON.parse(JSON.stringify(selectedValue));
        selectedData.push(_params);
      });
      const res = await GlobalApi.deletePasswordExtractionRules({
        passwordExtractionRules: selectedData,
      });
      if (res.data) {
        reset();
        // window.showNotification({
        //   titleMsg: t(
        //     'globalSetting.AttachmentPasswordGuessing.delete.success'
        //   ),
        //   severity: 'success',
        // });
      }
    } catch (error: any) {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    } finally {
      onClose();
      setDelLoading(false);
      await loadData();
    }
  };
  return (
    <Box px="4x">
      <DelModal
        visible={isOpen}
        onClose={onClose}
        onConfirm={confirmDel}
        loading={delLoading}
        title={delModalTitle}
        content={delModalContent}
        withEditPermission={withEditPermission}
      ></DelModal>
      <AddModal
        visible={isAddModalOpen}
        onClose={toggleAddModalOpen}
        onSave={handleAdd}
        title={t('globalSetting.AttachmentPasswordGuessing.addModal.title')}
      />
      <EditModal
        visible={isEditModalOpen}
        onClose={toggleEditModalOpen}
        onSave={handleEdit}
        selectedExtractionRule={selectedExtractionRule}
        title={t('globalSetting.AttachmentPasswordGuessing.editModal.title')}
      />
      <Flex m="16px 0px">
        <Text fontSize="lg" lineHeight="lg" color={colorStyle.color.primary}>
          {t('globalSetting.AttachmentPasswordGuessing.tab.title')}
        </Text>
      </Flex>
      <Flex m="16px 0px">
        <Text
          color={colorStyle.color.secondary}
          lineHeight="lg"
          maxWidth={1000}
        >
          {t('globalSetting.AttachmentPasswordGuessing.description')}
        </Text>
      </Flex>
      <Flex alignItems="center">
        <Switch
          disabled={!withEditPermission}
          pr="1x"
          id="approvedHeaderFields"
          checked={enablePasswordExtraction}
          onChange={async (e: any) => {
            await switchStatus(e.target.checked);
          }}
        />
        <Text>{t('globalSetting.AttachmentPasswordGuessing.tab.title')}</Text>
      </Flex>
      <Flex m="16px 0px">
        <Text>
          <Space>{t('globalSetting.note.title')}</Space>
          <Space width="1x"></Space>
          <Space color={colorStyle.color.secondary}>
            {t(`globalSetting.AttachmentPasswordGuessing.note`, {
              number: numberRule,
            })}
          </Space>
        </Text>
      </Flex>
      <Flex alignItems="center">
        {selectedRowCount === 1 ? (
          <Box mr={16}>
            <Stack direction="row">
              <Button
                variant="primary"
                onClick={onEdit}
                disabled={!withEditPermission || selectedRowCount !== 1}
              >
                {t('common.action.label.edit')}
              </Button>
            </Stack>
          </Box>
        ) : null}
        {selectedRowCount !== 0 ? (
          <Box mr={16}>
            <Stack direction="row">
              <LoadingBtn
                variant="secondary"
                mr="4x"
                onClick={onDelete}
                disabled={!withEditPermission || selectedRowCount === 0}
              >
                {t('reportCommon.buttons.delete')}
              </LoadingBtn>
              <IconButton onClick={reset}>
                <Icon as={CloseSIcon}></Icon>
              </IconButton>
              <Box lineHeight="2rem">
                <Text color={colorStyle.color.tertiary}>
                  {selectedRowCount}&nbsp;
                  {t('common.label.selected')}
                </Text>
              </Box>
            </Stack>
          </Box>
        ) : null}
        {selectedRowCount === 0 ? (
          <Stack direction="row">
            <Button
              variant="primary"
              onClick={onAdd}
              disabled={!withEditPermission || allItems.length >= 10}
            >
              {t('common.action.label.add')}
            </Button>
          </Stack>
        ) : null}
        <Space width="12px" />
        <SearchInput
          placeholder={t(
            'globalSetting.AttachmentPasswordGuessing.column.precedingText'
          )}
          onChange={(e: any) => {
            search(e.target.value);
          }}
          onClearInput={() => {
            search('');
          }}
          width="374px"
        />
      </Flex>
      <Table mt={28} layout="flexbox" {...tableStyleProps}>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableHeaderRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const styleProps = {
                  minWidth: header.column.columnDef.minSize,
                  width: header.getSize(),
                };
                return (
                  <TableHeaderCell key={header.id} {...styleProps}>
                    {header.isPlaceholder ? null : (
                      <SmartTooltip>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </SmartTooltip>
                    )}
                  </TableHeaderCell>
                );
              })}
            </TableHeaderRow>
          ))}
        </TableHeader>
        <TableBody overflowY="scroll">
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              _hover={{
                backgroundColor: hoverBackgroundColor,
              }}
            >
              {row.getVisibleCells().map((cell) => {
                const styleProps = {
                  minWidth: cell.column.columnDef.minSize,
                  width: cell.column.getSize(),
                };
                return (
                  <TableCell key={cell.id} {...styleProps}>
                    <SmartTooltip>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </SmartTooltip>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
export default AttachmentPasswordGuessing;
